import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import RequirementList from "../requirements/RequirementList";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { getFramework, deleteFramework } from "../../api/frameworks";
import { useToast } from "../../components/common/ToastProvider";
import Button from "../../ui/Button";

export default function FrameworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["framework", id],
    queryFn: () => getFramework(id),
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteFramework(id),
    onSuccess: () => {
      success("Framework deleted");
      queryClient.invalidateQueries({ queryKey: ["frameworks"] });
      navigate("/frameworks");
    },
    onError: (err) => showError(err?.detail || "Failed to delete framework"),
  });

  const breadcrumbItems = [
    { label: "Frameworks", to: "/frameworks" },
    { label: data?.name || "Detail" },
  ];

  return (
    <div>
      <Breadcrumbs items={breadcrumbItems} />
      {isLoading && <Loader />}
      {isError && <div>Error: {error?.detail || "Failed to load framework"} </div>}
      {data && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <h1>{data.name}</h1>
            <div style={{ display: "flex", gap: "8px" }}>
              <Button type="button" data-testid="edit-framework" onClick={() => navigate(`/frameworks/${id}/edit`)} variant="secondary">
                Edit
              </Button>
              <Button type="button" data-testid="delete-framework" onClick={() => setConfirmOpen(true)} variant="destructive">
                Delete
              </Button>
            </div>
          </div>
          <p>
            <strong>Code:</strong> {data.code}
          </p>
          <p>
            <strong>Description:</strong> {data.description || "-"}
          </p>
          <h2 style={{ marginTop: "16px" }}>Requirements</h2>
          <RequirementList frameworkId={data.id} />
        </div>
      )}
      {!isLoading && !data && <EmptyState title="Framework not found" />}
      <DestructiveConfirm
        actionId="FRAMEWORK_DELETE"
        open={confirmOpen}
        resourceDisplayName={data?.name}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          deleteMutation.mutate();
        }}
      />
    </div>
  );
}
